import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, useParams } from "react-router-dom";
import logoIcon from "../assets/images/logo-icon.svg";
import { resetPassword } from "../auth";

const ResetPassword = () => {
    const { t, i18n } = useTranslation();
    const { uid, token } = useParams();

    const [password, setPassword] = useState("");
    const [showPassword, setShowPassword] = useState(false);
    const [passwordError, setPasswordError] = useState("");
    const [formError, setFormError] = useState("");
    const [resetSuccessful, setResetSuccessful] = useState(false);
    const [submitButtonIsDisabled, setSubmitButtonIsDisabled] = useState(false);

    const handleLanguageChange = (e) => {
        i18n.changeLanguage(e.target.value);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setPasswordError("");
        setFormError("");

        if (password.length < 8) {
            setPasswordError(t("auth.passwordTooShort"));
            return;
        }

        setSubmitButtonIsDisabled(true);

        try {
            await resetPassword(uid, token, password);
            setResetSuccessful(true);
        } catch (error) {
            setSubmitButtonIsDisabled(false);
            if (error.fieldErrors?.new_password) {
                setPasswordError(error.fieldErrors.new_password[0]);
            } else {
                setFormError(t("auth.resetLinkInvalid"));
            }
        }
    };

    return (
        <div className="flex justify-center bg-white pt-8 sm:pt-16 px-2 sm:p-2">
            <div className="w-full max-w-md space-y-4 sm:space-y-6 p-4 sm:p-8 border border-gray-200 rounded-xl shadow-sm relative">
                {/* Language Switcher */}
                <div className="absolute top-4 right-4 flex items-center gap-2">
                    <span
                        className={`cursor-pointer ${
                            i18n.language === "en" ? "font-bold" : ""
                        } hover-underline underline-black`}
                        onClick={() => handleLanguageChange({ target: { value: "en" } })}
                    >
                        EN
                    </span>
                    <span>|</span>
                    <span
                        className={`cursor-pointer ${
                            i18n.language === "de" ? "font-bold" : ""
                        } hover-underline underline-black`}
                        onClick={() => handleLanguageChange({ target: { value: "de" } })}
                    >
                        DE
                    </span>
                </div>
                <div className="flex justify-center">
                    <Link to="/">
                        <img src={logoIcon} alt="AusspracheTrainer Logo" className="h-24 w-24" />
                    </Link>
                </div>
                <h2 className="text-center text-2xl text-gray-900">
                    {t("auth.resetPasswordTitle")}
                </h2>

                {resetSuccessful ? (
                    <>
                        <div className="bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded-lg text-sm">
                            {t("auth.resetSuccessful")}
                        </div>
                        <p className="text-center text-sm text-gray-500">
                            <Link to="/login" className="text-purple-500 hover:underline">
                                {t("auth.backToLogin")}
                            </Link>
                        </p>
                    </>
                ) : (
                    <>
                        <p className="text-center text-sm text-gray-500">
                            {t("auth.resetPasswordDetails")}
                        </p>
                        {formError && (
                            <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm">
                                {formError}{" "}
                                <Link
                                    to="/forgot-password"
                                    className="text-purple-500 hover:underline"
                                >
                                    {t("auth.requestNewLink")}
                                </Link>
                            </div>
                        )}
                        <form className="space-y-4" onSubmit={handleSubmit}>
                            <div>
                                <label
                                    htmlFor="password"
                                    className="block text-sm font-medium text-gray-700 mb-1"
                                >
                                    {t("auth.newPassword")}{" "}
                                    <span className="text-xs text-gray-500">
                                        (min. 8 {t("auth.characters")})
                                    </span>
                                </label>
                                <div className="relative">
                                    <input
                                        id="password"
                                        type={showPassword ? "text" : "password"}
                                        placeholder="••••••••"
                                        className="w-full px-4 py-2 border border-gray-300 rounded-lg text-sm"
                                        value={password}
                                        onChange={(e) => {
                                            setPassword(e.target.value);
                                            setPasswordError("");
                                        }}
                                        required
                                    />
                                    <button
                                        type="button"
                                        className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-md p-1"
                                        onClick={() => setShowPassword(!showPassword)}
                                    >
                                        {showPassword ? (
                                            <svg
                                                xmlns="http://www.w3.org/2000/svg"
                                                fill="none"
                                                viewBox="0 0 24 24"
                                                strokeWidth={1.5}
                                                stroke="currentColor"
                                                className="size-4"
                                            >
                                                <path
                                                    strokeLinecap="round"
                                                    strokeLinejoin="round"
                                                    d="M2.036 12.322a1.012 1.012 0 0 1 0-.639C3.423 7.51 7.36 4.5 12 4.5c4.638 0 8.573 3.007 9.963 7.178.07.207.07.431 0 .639C20.577 16.49 16.64 19.5 12 19.5c-4.638 0-8.573-3.007-9.963-7.178Z"
                                                />
                                                <path
                                                    strokeLinecap="round"
                                                    strokeLinejoin="round"
                                                    d="M15 12a3 3 0 1 1-6 0 3 3 0 0 1 6 0Z"
                                                />
                                            </svg>
                                        ) : (
                                            <svg
                                                xmlns="http://www.w3.org/2000/svg"
                                                fill="none"
                                                viewBox="0 0 24 24"
                                                strokeWidth={1.5}
                                                stroke="currentColor"
                                                className="size-4"
                                            >
                                                <path
                                                    strokeLinecap="round"
                                                    strokeLinejoin="round"
                                                    d="M3.98 8.223A10.477 10.477 0 0 0 1.934 12C3.226 16.338 7.244 19.5 12 19.5c.993 0 1.953-.138 2.863-.395M6.228 6.228A10.451 10.451 0 0 1 12 4.5c4.756 0 8.773 3.162 10.065 7.498a10.522 10.522 0 0 1-4.293 5.774M6.228 6.228 3 3m3.228 3.228 3.65 3.65m7.894 7.894L21 21m-3.228-3.228-3.65-3.65m0 0a3 3 0 1 0-4.243-4.243m4.242 4.242L9.88 9.88"
                                                />
                                            </svg>
                                        )}
                                    </button>
                                </div>
                                {passwordError && (
                                    <p className="mt-1 text-sm text-red-600">{passwordError}</p>
                                )}
                            </div>
                            <button
                                disabled={submitButtonIsDisabled}
                                type="submit"
                                className="w-full bg-primary hover:bg-purple-500 text-white py-2 rounded-md"
                            >
                                {t("auth.setNewPassword")}
                            </button>
                        </form>
                    </>
                )}
            </div>
        </div>
    );
};

export default ResetPassword;
